/**
 * The responsibility of this component is to renders a form for creating or editing a single day event with optional time slots
 *
 * This component handles:
 * 1. Date and time selection for the event
 * 2. Option to enable/disable time slots
 * 3. Time slot management with customizable durations
 * 4. Expandable UI for displaying and manipulating slots
 *
 * @returns {JSX.Element} A form for single day event configuration with optional time slots
 */

import React from 'react';
import { FormControlLabel, RadioGroup, Radio, Divider, FormHelperText } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { CirclePlusIcon, TrashIcon } from '../../../../../../../../icons/FontAwesomeIcon';
import dayjs from 'dayjs';
import MuiDatePicker from '../../../../../../../../components/FormFields/MuiDatePicker';
import MuiTimePicker from '../../../../../../../../components/FormFields/MuiTimePicker';
import CustomSlotInput from '../../../../../../../../components/FormFields/CustomSlotInput';
import MuiSelectOption from '../../../../../../../../components/FormFields/MuiSelectOptions';
import { formatSlotDate } from '../../../../../../../../utils/dateFormatter';
import { H6, H5, Body1, Body2, Body3 } from '../../../../../../../../themes/typography';
import '../../../../../../../../styles/components/OneTimeSlot.css';

function SingleDayEvent({
    formik,
    expanded,
    handleExpandChange,
    addSlot,
    deleteSlot,
    options,
    onSlotOptionChange,
    slotOptionValue,
    isHourSelected,
    customDurationValue,
    setIsHourSelected,
    handleCustomDurationChange,
    durationError,
    isEditModeOn
}) {
    let emptyTime =
        formik.values.startTime === null ||
        formik.values.endTime === null ||
        formik.values.startDate === null ||
        dayjs(formik.values.startTime, 'HH:mm').isSame(dayjs(formik.values.endTime, 'HH:mm'));

    return (
        <form
            className="onetime-event-schedule-container"
            onSubmit={formik.handleSubmit}
            noValidate
        >
            <div className="start-date-schedule-wrapper">
                <div className="start-date-schedule-container">
                    <div className="schedule-selection-title">
                        <H6>Select Event Date and Time</H6>
                    </div>
                    <div className="single-day-event-inputs">
                        <div className="single-day-event-input">
                            <MuiDatePicker
                                fullWidth
                                id="startDate"
                                name="startDate"
                                label="Start Date *"
                                format="DD/MM/YYYY"
                                value={formik.values.startDate}
                                disabled={!isEditModeOn}
                                onChange={(date) => formik.setFieldValue('startDate', date)}
                                error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                                helperText={formik.touched.startDate && formik.errors.startDate}
                            />
                        </div>
                        <div className="single-day-event-input">
                            <MuiTimePicker
                                fullWidth
                                id="startTime"
                                name="startTime"
                                label="Start Time *"
                                format="HH:mm"
                                minTime={
                                    dayjs(formik.values.startDate, 'DD/MM/YYYY').isSame(
                                        dayjs(),
                                        'day'
                                    )
                                        ? dayjs().add(1, 'hour')
                                        : dayjs().startOf('day')
                                }
                                value={formik.values.startTime}
                                disabled={!isEditModeOn}
                                onChange={(time) => formik.setFieldValue('startTime', time)}
                                error={formik.touched.startTime && Boolean(formik.errors.startTime)}
                                helperText={formik.touched.startTime && formik.errors.startTime}
                            />
                        </div>
                        <div className="single-day-event-input">
                            <MuiTimePicker
                                fullWidth
                                id="endTime"
                                name="endTime"
                                label="End Time *"
                                format="HH:mm"
                                minTime={
                                    dayjs(formik.values.startDate)
                                        ? dayjs(formik.values.startTime).add(30, 'minute')
                                        : dayjs().startOf('day')
                                }
                                value={formik.values.endTime}
                                disabled={!isEditModeOn}
                                onChange={(time) => formik.setFieldValue('endTime', time)}
                                error={formik.touched.endTime && Boolean(formik.errors.endTime)}
                                helperText={formik.touched.endTime && formik.errors.endTime}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="event-has-time-slots-container">
                <div className="event-has-slot-wrapper">
                    <div className="has-slot-text-container">
                        <H5 fontWeight={500}>This event has time slots</H5>
                    </div>
                    <div className="has-slot-options-selection">
                        <RadioGroup
                            row
                            aria-labelledby="has-slot-radio-buttons-group-label"
                            name="isEventHasSlot"
                            sx={{ gap: '1.5rem' }}
                            value={formik.values.isEventHasSlot}
                            onChange={(e) =>
                                formik.setFieldValue(
                                    'isEventHasSlot',
                                    String(e.target.value) === 'true'
                                )
                            }
                        >
                            <FormControlLabel
                                value={true}
                                sx={{ gap: '0.75rem' }}
                                control={
                                    <Radio
                                        sx={{ padding: '0' }}
                                        disabled={emptyTime || !isEditModeOn}
                                    />
                                }
                                label="Yes"
                            />
                            <FormControlLabel
                                value={false}
                                sx={{ gap: '0.75rem' }}
                                control={
                                    <Radio
                                        sx={{ padding: '0' }}
                                        disabled={emptyTime || !isEditModeOn}
                                    />
                                }
                                label="No"
                            />
                        </RadioGroup>
                    </div>
                </div>
            </div>

            {formik.values.isEventHasSlot && (
                <div className="one-time-slot">
                    <div className="one-time-slot-header">
                        <div className="one-time-slot-title-icon">
                            <H5>{formatSlotDate(formik.values.startDate)}</H5>
                            <div className="expand-container">
                                <div onClick={handleExpandChange}>
                                    <ExpandMore
                                        sx={{
                                            color: '#f5f5f5',
                                            fontSize: '1.5rem',
                                            transition: 'all 0.3s ease-in-out',
                                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            className="number-of-slots"
                            style={{ display: expanded ? 'none' : '' }}
                        >
                            <div>
                                <Body3>
                                    Number of slots: {''}
                                    <span style={{ color: '#f8a21e' }}>
                                        {formik.values.slots?.length}
                                    </span>
                                </Body3>
                            </div>
                        </div>
                    </div>
                    <div
                        className="one-time-slot-expanded"
                        style={{ display: !expanded ? 'none' : '' }}
                    >
                        <div className="number-of-slots">
                            <div>
                                <div className="duration-wrapper">
                                    <MuiSelectOption
                                        id="slotDuration"
                                        label="Slot Duration"
                                        value={slotOptionValue}
                                        options={options}
                                        onChange={onSlotOptionChange}
                                        disabled={!isEditModeOn}
                                    />
                                    <Body2>OR</Body2>
                                    <CustomSlotInput
                                        isHourSelected={isHourSelected}
                                        value={customDurationValue}
                                        setIsHourSelected={setIsHourSelected}
                                        onChange={handleCustomDurationChange}
                                        disabled={!isEditModeOn}
                                    />
                                </div>
                                <FormHelperText error={durationError}>
                                    {durationError
                                        ? 'Custom duration should not be less than 5 minutes'
                                        : ''}
                                </FormHelperText>
                            </div>

                            <div
                                className="add-one-time-slots-container"
                                style={{ pointerEvents: isEditModeOn ? 'auto' : 'none' }}
                                onClick={() => {
                                    if (!isEditModeOn) return;
                                    addSlot();
                                }}
                            >
                                <Body1>Add Slots</Body1>
                                <CirclePlusIcon style={{ color: '#f8a21e' }} />
                            </div>
                        </div>
                        <div className="one-time-slots-listing-container">
                            {formik.values.slots?.map((slot, index) => (
                                <React.Fragment key={index}>
                                    <div className="slot-children-node-wrapper">
                                        <div className="slot-number-container">
                                            <Body1>Slot {index + 1}</Body1>
                                        </div>
                                        <div className="timing-input">
                                            <MuiTimePicker
                                                label="Start Time"
                                                disabled={!isEditModeOn}
                                                id={`slots.${index}.startTime`}
                                                name={`slots.${index}.startTime`}
                                                value={formik.values.slots[index].startTime}
                                                minTime={
                                                    index === 0
                                                        ? dayjs(formik.values.startTime, 'HH:mm') // event start time in first slot start time should be same
                                                        : formik.values.slots[index - 1].endTime
                                                }
                                                maxTime={dayjs(formik.values.startTime, 'HH:mm')} // end time of event
                                                error={formik.errors.slots?.[index]?.startTime}
                                                helperText={formik.errors.slots?.[index]?.startTime}
                                                onChange={(value) =>
                                                    formik.setFieldValue(
                                                        `slots.${index}.startTime`,
                                                        value
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="timing-input">
                                            <MuiTimePicker
                                                label="End Time"
                                                disabled={!isEditModeOn}
                                                id={`slots.${index}.endTime`}
                                                name={`slots.${index}.endTime`}
                                                value={formik.values.slots[index].endTime}
                                                minTime={dayjs(
                                                    formik.values.slots[index].startTime,
                                                    'HH:mm'
                                                ).add(5, 'minute')}
                                                maxTime={dayjs(formik.values.endTime, 'HH:mm')} // end time of event
                                                error={formik.errors.slots?.[index]?.endTime}
                                                helperText={formik.errors.slots?.[index]?.endTime}
                                                onChange={(value) =>
                                                    formik.setFieldValue(
                                                        `slots.${index}.endTime`,
                                                        value
                                                    )
                                                }
                                            />
                                        </div>
                                        <div
                                            style={{
                                                pointerEvents: isEditModeOn ? 'auto' : 'none'
                                            }}
                                            className="delete-container"
                                            onClick={() => {
                                                if (!isEditModeOn) return;
                                                deleteSlot(index);
                                            }}
                                        >
                                            <TrashIcon
                                                style={{
                                                    fontSize: '1.25rem',
                                                    color: '#f8a21e'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {index < formik.values.slots.length - 1 && (
                                        <Divider sx={{ borderColor: 'var(--divider-color)' }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
}

export default SingleDayEvent;
